import { usePaginationWithComponent } from "hooks/usePaginationWithComponent";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import {
  getRequest,
  postRequest,
  patchRequest,
  deleteRequest,
} from "services/api";
// import { LIMIT_DATA_PER_PAGE } from "utils/constants";
import { buildQueryString } from "utils/queryHelpers";

const ATVContext = createContext();

export const ATVProvider = ({ children }) => {
  const [atvs, setAtvs] = useState({
    data: [],
    pagination: null,
    error: null,
    loading: false,
  });

  const [filterOptions, setFilterOptions] = useState({
    search: "",
    currentPage: 1,
    totalPages: 1,
  });

  const queryString = buildQueryString({
    search: filterOptions.search || "",
    page: filterOptions.currentPage,
    // limit: LIMIT_DATA_PER_PAGE,
    // tambahkan include jika diperlukan
    // limit: 100,
    all: 1,
    include: "status",
  });

  const handleSearch = (e) => {
    if (e.key === "Enter") {
      console.log("search", e.target.value);
      setFilterOptions((prevState) => ({
        ...prevState,
        search: e.target.value,
      }));
    }
  };

  const fetchAtvs = useCallback(async () => {
    setAtvs((prevState) => ({
      ...prevState,
      loading: true,
    }));

    try {
      const response = await getRequest(`/atv${queryString}`);
      if (response.success) {
        setAtvs({
          data: response.data,
          pagination: response.pagination,
          error: null,
          loading: false,
        });

        setFilterOptions((prev) => ({
          ...prev,
          totalPages: response.pagination?.last_page,
        }));
      }
    } catch (error) {
      setAtvs({
        data: [],
        pagination: null,
        error: error.message,
        loading: false,
      });
    }
  }, [queryString]);

  useEffect(() => {
    fetchAtvs();
  }, [fetchAtvs]);

  const createATV = useCallback(
    async (atvData) => {
      try {
        const response = await postRequest("/atv", atvData);
        if (response.success) {
          fetchAtvs(); // Refresh the list
          return { success: true };
        }
        return { success: false, error: response.message };
      } catch (error) {
        return { success: false, error: error.message };
      }
    },
    [fetchAtvs]
  );

  const updateATV = useCallback(
    async (id, atvData) => {
      try {
        const response = await patchRequest(`/atv/${id}`, atvData);
        if (response.success) {
          fetchAtvs(); // Refresh the list
          return { success: true };
        }
        return { success: false, error: response.message };
      } catch (error) {
        return { success: false, error: error.message };
      }
    },
    [fetchAtvs]
  );

  const deleteATV = useCallback(
    async (id) => {
      try {
        const response = await deleteRequest(`/atv/${id}`);
        if (response.success) {
          fetchAtvs(); // Refresh the list
          return { success: true };
        }
        return { success: false, error: response.message };
      } catch (error) {
        return { success: false, error: error.message };
      }
    },
    [fetchAtvs]
  );

  const handlePageChange = (page) => {
    setFilterOptions((prevState) => ({
      ...prevState,
      currentPage: page,
    }));
  };

  const totalPages = atvs.pagination?.last_page || 1;

  const Pagination = usePaginationWithComponent({
    totalPages: totalPages,
    currentPage: filterOptions.currentPage,
    onPageChange: handlePageChange,
    visiblePages: 5,
  });

  return (
    <ATVContext.Provider
      value={{
        atvs,
        filterOptions,
        handleSearch,
        refetch: fetchAtvs,
        createATV,
        updateATV,
        deleteATV,
        Pagination,
      }}
    >
      {children}
    </ATVContext.Provider>
  );
};

export const useATV = () => {
  const context = useContext(ATVContext);
  if (!context) {
    throw new Error("useATV must be used within ATVProvider");
  }

  const fetchOptions = async (inputValue) => {
    const response = await getRequest(`/atv?search=${inputValue}`);
    if (response.success) {
      return response.data.map((atv) => ({
        value: atv.id,
        label: `${atv.name} - ${atv.plate_number}`,
      }));
    }
    return [];
  };

  return { ...context, fetchOptions };
};
